import {AmountDisplay, cn, Typography} from '@sollapay/ui';
import {formatTime, parseMoney} from '@sollapay/utils';

import {TransactionStatusBadge} from './TransactionStatusBadge';

import type {TransactionStatus} from '@sollapay/enums';
import type {FC, ReactNode} from 'react';

/** Grid the rows join via subgrid — the name column fits the longest name in the list. */
export const TRANSACTIONS_GRID_CLASS = 'grid grid-cols-[auto_6rem_11rem_9.75rem_1fr]';

interface BaseTransactionRowProps {
  directionIcon: ReactNode;
  nameSection: ReactNode;
  badgeSection: ReactNode;
  status: TransactionStatus;
  effectiveDate: string;
  amount: string;
  isCredit: boolean;
  isInflight: boolean;
  percent?: string | null;
}

export const BaseTransactionRow: FC<BaseTransactionRowProps> = ({
  directionIcon,
  nameSection,
  badgeSection,
  status,
  effectiveDate,
  amount,
  isCredit,
  isInflight,
  percent
}) => {
  const amountPrefix = isCredit ? '+' : '-';
  const formattedAmount = parseMoney(amount).formatNIS({symbol: true});

  return (
    <div
      data-slot="transaction-row"
      className="col-span-full grid h-18 grid-cols-subgrid items-center rounded-xl border border-border-subtle bg-background-surface shadow-sm"
    >
      <div className="flex min-w-0 items-center gap-3 px-6">
        <div className="shrink-0">{directionIcon}</div>
        <div className="flex min-w-0 flex-col items-start *:max-w-full">{nameSection}</div>
      </div>

      <div className="flex items-center px-6">
        <Typography size="sm" color="tertiary">
          {formatTime(effectiveDate)}
        </Typography>
      </div>

      <div className="flex items-center px-6">{badgeSection}</div>

      <div className="flex items-center px-6">
        <TransactionStatusBadge status={status} />
      </div>

      <div className={cn('flex items-center px-6', !isInflight && 'justify-end')}>
        {isInflight ? (
          <div data-slot="inflight-amount" className="flex items-center gap-1" dir="ltr">
            {percent != null && (
              <Typography size="xs" color="tertiary">
                ({percent}%)
              </Typography>
            )}
            <div className="flex items-center gap-1">
              <Typography size="md" weight="semibold">
                {amountPrefix}
              </Typography>
              <Typography size="sm" weight="semibold">
                {formattedAmount}
              </Typography>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-end">
            <AmountDisplay amount={amount} variant={isCredit ? 'credit' : 'debit'} />
            {percent != null && (
              <Typography size="xs" color="tertiary">
                {percent}%
              </Typography>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
